/**
 * BookingWizardContext — Phase 3 §4 (E2).
 *
 * Lifts the wizard's current `step` so the surrounding CustomerShell
 * (`CustomerTopNav`) can render a progress indicator mid-wizard
 * instead of the 4 nav-link temptations.
 *
 * IMPORTANT — Why a module-scoped store instead of React Context:
 *
 *   The CustomerShell render tree is:
 *
 *     <CustomerShell>
 *       <CustomerTopNav />     ← needs to read step
 *       <main>
 *         <Outlet />            ← renders <BookingWizard /> which owns step
 *       </main>
 *     </CustomerShell>
 *
 *   CustomerTopNav is a SIBLING of <Outlet>, not a descendant — so a
 *   React Context Provider mounted inside <BookingWizard> can't
 *   propagate up to <CustomerTopNav>. Lifting step state to a route
 *   wrapper would force changes to every `setStep` callsite (7 of
 *   them) and tangle wizard ownership with shell wiring.
 *
 *   A module-scoped signal store cleanly sidesteps the tree topology:
 *   BookingWizard writes `step` on every render, CustomerTopNav
 *   subscribes — both work regardless of where they sit in the tree.
 *   The signal also auto-clears when BookingWizard unmounts, so the
 *   topnav reverts to normal nav on Confirmation/Success/Cancel.
 *
 *   The hook contract from the discovery doc still holds: returns
 *   null when no wizard is mounted, `{step, totalSteps}` when active.
 */
import { useEffect, useState } from "react";

let currentSnapshot = null;
const listeners = new Set();

function notify() {
  listeners.forEach((fn) => fn(currentSnapshot));
}

/**
 * Called by `<BookingWizard>` whenever its internal `step` state
 * changes — and called with `null` on unmount to clear the signal.
 */
export function setBookingWizardSnapshot(snapshot) {
  currentSnapshot = snapshot;
  notify();
}

/**
 * Hook used by `<CustomerTopNav>` to read the live wizard snapshot.
 * Returns `null` when no wizard is mounted (normal nav renders).
 * Returns `{step, totalSteps}` when active.
 */
export function useBookingWizard() {
  const [snap, setSnap] = useState(currentSnapshot);
  useEffect(() => {
    listeners.add(setSnap);
    setSnap(currentSnapshot);
    return () => listeners.delete(setSnap);
  }, []);
  return snap;
}
